import { queryKeys } from '@query/keys'
import { usePaginatedQuery } from '@query/usePaginatedQuery'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useModal } from '@context/ModalsContext'
import { usePathHistoryContext } from '@context/PathHistoryContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { FileData } from '@ui/components/FileUpload/types'

import { useAccountActionAccessCheck } from '@utils/AccountAccess/accountAccessCheck'
import { useApiClient } from '@utils/ApiClient'
import { santizeBase64 } from '@utils/base64Helper'
import { ClientApiClient } from '@utils/clientApi'

import { BankAccountFormValues } from '@model/BankAccount'
import { DocumentCategoryType } from '@model/DocumentCategories'
import { DocumentCategoryOptionType } from '@model/DocumentCategoryOptionType'
import { DocumentStatus } from '@model/DocumentStatus'
import { WalletCurrency, WalletTypeEnum } from '@model/WalletDto'

import { Loading } from '@shared/components/Loading/Loading'
import { AlreadyUploadedModal } from '@shared/modals/AlreadyUploaded.modal'
import { SuccessModal as SharedSuccessModal } from '@shared/modals/Success.modal'

import {
  getDocUploadFailMessage,
  isDocError,
} from '../DocumentManagementUpload/documentManagementUploadStatuses'
import { CreateBankAccountForm } from './CreateBankAccountForm'

export interface CreateBankAccountFormValues extends Omit<BankAccountFormValues, 'documentId'> {
  availableCurrencies: WalletCurrency[]
  document?: FileData
  previouslyUploadedDocumentId?: string
}

const useDocuments = () => {
  const apiClient = useApiClient(ClientApiClient)

  const locale = useSessionLanguage()

  const search = `((Category == ${DocumentCategoryType.Payment}) OR (Category == ${DocumentCategoryType.Additional})) AND ((Type == ${DocumentCategoryOptionType.BankStatement}) OR (Type == ${DocumentCategoryOptionType.ProofOfDeposit})) AND (Status == ${DocumentStatus.Approved})`

  const searchKey = { search, locale }

  const query = usePaginatedQuery({
    queryKey: queryKeys.document.list(searchKey, locale),
    queryFn: (pageQuery) =>
      apiClient.getDocuments({
        ...pageQuery,
        search,
      }),
  })
  return { ...query, forceRefresh: query.forceRefresh }
}

export const CreateBankAccountPage: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  const [createdBankAccountId, setCreatedBankAccountId] = useState<string>()
  const successModal = useModal(SharedSuccessModal)
  const locale = useSessionLanguage()
  const { data: documents, isLoading } = useDocuments()
  const { navigateToPreviousPath } = usePathHistoryContext()
  const alreadyUploadedModal = useModal(AlreadyUploadedModal)

  const { checkAccountTradersRoomAccess } = useAccountActionAccessCheck()

  const { data: validationRules, isLoading: isLoadingValidationRules } = useQuery({
    queryKey: queryKeys.profile.bankAccountValidationOptions(),
    queryFn: () => apiClient.getBankAccountValidationOptions(),
  })

  const handleSubmitBankAccount = async (values: CreateBankAccountFormValues) => {
    try {
      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        availableCurrencies,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        documents,
        document,
        previouslyUploadedDocumentId,
        ...bankAccountData
      } = values
      const createdBankAccount = await apiClient.addBankAccount({
        ...bankAccountData,
        document: document
          ? {
              file: santizeBase64(document?.base64Content),
              fileName: document?.fileName || '',
            }
          : undefined,
        documentId: previouslyUploadedDocumentId,
      })
      await queryClient.invalidateQueries({ queryKey: ['profile'] })

      setCreatedBankAccountId(createdBankAccount.id)

      if (location?.state?.walletId) {
        successModal.open({
          title: t('Success'),
          description: <span>{t('Bank Account Added')}</span>,
          primaryLabel: t('Wallet.Back to Withdrawal Form'),
          onPrimaryClick: goToWithdrawal,
        })
      } else {
        successModal.open({
          title: t('Success'),
          description: <span>{t('Bank Account Added')}</span>,
          onPrimaryClick: () => navigate('/profile/bank-accounts'),
        })
      }
    } catch (e: unknown) {
      const errorCode = (e as any).response?.response?.data?.code
      if (isDocError(errorCode)) {
        alreadyUploadedModal.open({ description: t(getDocUploadFailMessage(errorCode)) })
      }
      console.error({ e })
    }
  }

  const goToWithdrawal = () => {
    checkAccountTradersRoomAccess(() => {
      const route =
        location.state.walletType === WalletTypeEnum.IB ? 'introducing-broker' : 'traders-room'
      const localeChanged = locale !== location.state.locale

      navigate(`/dashboard/${route}/wallets/${location.state.walletId}/withdrawal`, {
        state: {
          values: location.state?.values,
          bankAccountId: createdBankAccountId,
          localeChanged,
          resetFields: false,
        },
      })
    })
  }

  const handleCancel = () => {
    if (location?.state?.walletId) {
      goToWithdrawal()
    } else {
      navigateToPreviousPath()
    }
  }

  return (
    <Loading isLoading={isLoading || isLoadingValidationRules} showLoadingIcon>
      <CreateBankAccountForm
        documents={documents}
        validationRules={validationRules}
        onSubmit={handleSubmitBankAccount}
        onCancelNewBank={handleCancel}
      />
    </Loading>
  )
}
